import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Building2,
  MapPin,
  Phone,
  Mail,
  FileText,
  CreditCard,
  User,
  Plus,
} from "lucide-react";

const SupplierMainMasEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [supplierCode, setSupplierCode] = useState("");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [supplierType, setSupplierType] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [ranking, setRanking] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [remarks, setRemarks] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [tin, setTIN] = useState("");
  const [gst, setGST] = useState("");
  const [pan, setPAN] = useState("");
  const [tallyHeader, setTallyHeader] = useState("");
  const [selectedTab, setSelectedTab] = useState("statutory");
  const [statutoryList, setStatutoryList] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [contactList, setContactList] = useState([]);
  const [divStateVisible, setDivStateVisible] = useState(false);
  const [divDistrictVisible, setDivDistrictVisible] = useState(false);
  const [divCityVisible, setDivCityVisible] = useState(false);
  const [supplierMainId, setSupplierMainId] = useState("");
  const [stateText, setStateText] = useState("");
  const [districtText, setDistrictText] = useState("");
  const [cityText, setCityText] = useState("");
  const [loading, setLoading] = useState(false);

const supplier = location.state?.supplier || null;
// ✅ FIX: confirmed via usp_INVBINDSupplierMainMasterGrid — the row passed
// from the listing page carries the PK as fld_suppliermainid, not
// fld_supplierid. That mismatch was causing supplierId to resolve to ""
// and the edit fetch to 404 with "Failed to load supplier data".
const supplierId =
  supplier?.fld_suppliermainid ||
  supplier?.fld_supplierid ||
  supplier?.supplierid ||
  location.state?.supplierid ||
  "";

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const api = useMemo(
    () =>
      axios.create({
        baseURL: "https://nissi-india.com/api",
        headers: commonHeaders,
      }),
    [commonHeaders]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const resetForm = () => {
    setSupplierCode("");
    setSupplierType("");
    setSelectedCountry("");
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedCity("");
    setRanking("");
    setSupplierName("");
    setAddress1("");
    setAddress2("");
    setZipcode("");
    setPhone("");
    setMobile("");
    setEmail("");
    setRemarks("");
    setOpeningBalance("");
    setTIN("");
    setGST("");
    setPAN("");
    setTallyHeader("");
    setStates([]);
    setDistricts([]);
    setCities([]);
    setDivStateVisible(false);
    setDivDistrictVisible(false);
    setDivCityVisible(false);
    setStateText("");
    setDistrictText("");
    setCityText("");
  };

  const fetchCountries = async () => {
    try {
      const res = await api.get("/bindCountry");
      setCountries(res.data?.countryList || []);
    } catch (err) {
      toast.error("Error fetching countries");
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    try {
      const res = await api.get("/stateDropdownChanged", {
        params: { countryid: countryId },
      });
      setStates(res.data?.stateList || []);
    } catch (err) {
      toast.error("Error fetching states");
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId || stateId === "other") {
      setDistricts([]);
      return;
    }
    try {
      const res = await api.get("/distDropdownChanged", {
        params: { stateid: stateId },
      });
      setDistricts(res.data?.distList || []);
    } catch (err) {
      toast.error("Error fetching districts");
    }
  };

  const fetchCities = async (distId) => {
    if (!distId || distId === "other") {
      setCities([]);
      return;
    }
    try {
      const res = await api.get("/cityDropdownChanged", {
        params: { distid: distId },
      });
      setCities(res.data?.cityList || []);
    } catch (err) {
      toast.error("Error fetching cities");
    }
  };

  const fetchEditData = async (id) => {
    if (!id) {
      toast.error("Supplier ID missing");
      return;
    }

    setLoading(true);
    try {
      const res = await api.get("/getSupplierMainEditDetails", {
        params: { supplierid: id },
      });

      const data = res.data?.supplierDetails;
      if (!data) {
        toast.error("No data found for supplier");
        return;
      }

setSupplierMainId(data.fld_suppliermainid || "");
      setSupplierCode(data.fld_suppliercode || "");
      setSupplierName(data.fld_suppliername || "");
      setAddress1(data.fld_Address1 || "");
      setAddress2(data.fld_Address2 || "");
      setSelectedCountry(String(data.fld_Countryid || ""));
      setSelectedState(String(data.fld_stateid || ""));
      setSelectedDistrict(String(data.fld_Districtid || ""));
      setSelectedCity(String(data.fld_Cityid || ""));
      setZipcode(data.fld_ZipCode || "");
      setPhone(data.fld_phoneno || "");
      setMobile(data.fld_Mobile || "");
      setEmail(data.fld_Email || "");
      setRemarks(data.fld_Remarks || "");
      setOpeningBalance(data.fld_opening_balance || "");
      setTIN(data.fld_TIN || "");
      setGST(data.fld_CST || "");
      setPAN(data.fld_PAN || "");
      setRanking(String(data.fld_ranking || ""));
      setSupplierType(data.fld_VendorType || "");
      setTallyHeader(data.fld_tallyheader || "");

      if (data.fld_Countryid) await fetchStates(data.fld_Countryid);
      if (data.fld_stateid) await fetchDistricts(data.fld_stateid);
      if (data.fld_Districtid) await fetchCities(data.fld_Districtid);
    } catch (err) {
      console.error("API error:", err);
      toast.error("Failed to load supplier data");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatutoryList = async (id) => {
    if (!id) {
      setStatutoryList([]);
      return;
    }
    try {
      const res = await api.get("/bindSupplierStatutory", {
        params: { supplierid: id },
      });
      setStatutoryList(res.data?.statutoryList || []);
    } catch (err) {
      toast.error("Error fetching statutory details");
    }
  };

  const fetchBankList = async (id) => {
    if (!id) {
      setBankList([]);
      return;
    }
    try {
      const res = await api.get("/bindSupplierBank", {
        params: { supplierid: id },
      });
      setBankList(res.data?.bankList || []);
    } catch (err) {
      toast.error("Error fetching bank details");
    }
  };

  const fetchContactList = async (id) => {
    if (!id) {
      setContactList([]);
      return;
    }
    try {
      const res = await api.get("/bindSupplierContact", {
        params: { supplierid: id },
      });
      setContactList(res.data?.contactList || []);
    } catch (err) {
      toast.error("Error fetching contact details");
    }
  };

  const handleSubmit = async () => {
    if (!supplierMainId) {
      toast.error("Supplier main ID missing");
      return;
    }

    if (!supplierName || !supplierType || !address1 || !selectedCountry || !zipcode || !mobile || !tin) {
      toast.error("Please fill all required fields");
      return;
    }

    let finalStateId = selectedState;
    let finalDistrictId = selectedDistrict;
    let finalCityId = selectedCity;

    if (selectedState === "other" || selectedDistrict === "other" || selectedCity === "other") {
      try {
        const res = await api.post("/insertAddressCity", {
          countryid: selectedCountry,
          stateText,
          districtText,
          cityText,
          stateidDropdown: selectedState !== "other" ? selectedState : "0",
          districtidDropdown: selectedDistrict !== "other" ? selectedDistrict : "0",
          cityidDropdown: selectedCity !== "other" ? selectedCity : "0",
          divStateVisible: divStateVisible ? "none" : "inline",
          divDistrictVisible: divDistrictVisible ? "none" : "inline",
          divCityVisible: divCityVisible ? "none" : "inline",
        });

        finalStateId = res.data?.stateid || finalStateId;
        finalDistrictId = res.data?.districtid || finalDistrictId;
        finalCityId = res.data?.cityid || finalCityId;
      } catch (err) {
        console.error("Insert Address Error:", err);
        toast.error("Error inserting new address");
        return;
      }
    }

    const payload = {
      suppliermainid: supplierMainId,
      suppliername: supplierName,
      suppliercode: supplierCode,
      address1,
      address2,
      cityid: parseInt(finalCityId, 10) || 0,
      stateid: parseInt(finalStateId, 10) || 0,
      countryid: parseInt(selectedCountry, 10) || 0,
      districtid: parseInt(finalDistrictId, 10) || 0,
      zipcode,
      phoneno: phone,
      mobile,
      email,
      remarks,
      opening_balance: parseFloat(openingBalance) || 0,
      TIN: tin,
      CST: gst,
      PAN: pan,
      ranking: parseInt(ranking, 10) || 0,
      VendorType: supplierType,
      tallyheader: tallyHeader || "",
    };

    try {
      const res = await api.put("/updateSupplierMain", payload);

      const isSuccess =
        res.data?.success === true ||
        res.data?.status === "ok" ||
        res.data?.message?.toLowerCase()?.includes("success") ||
        res.data?.message?.toLowerCase()?.includes("updated");

      if (isSuccess) {
        toast.success("Supplier updated successfully");
        navigate(-1);
      } else {
        toast.error("Update failed");
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err?.response?.data?.error || "Error updating supplier");
    }
  };

  useEffect(() => {
    fetchCountries();
    if (supplierId) {
      fetchEditData(supplierId);
    } else {
      toast.error("Supplier ID not found");
    }
  }, []);

  useEffect(() => {
    const id = supplierMainId || supplierId;
    if (!id) return;

    if (selectedTab === "statutory") fetchStatutoryList(id);
    else if (selectedTab === "bank") fetchBankList(id);
    else if (selectedTab === "contact") fetchContactList(id);
  }, [selectedTab, supplierMainId]);

  const currentList =
    selectedTab === "statutory"
      ? statutoryList
      : selectedTab === "bank"
      ? bankList
      : contactList;

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Edit Supplier</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header + Back */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Edit Supplier</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Update supplier master details
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
              isDark
                ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Main Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div
            className={`px-6 py-4 border-b flex items-center gap-3 ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
            <h2 className="text-xl font-bold">Supplier Details</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
              {/* Left Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Supplier Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="Enter supplier name"
                  />
                </div>

                <div>
                  <label className={labelClass}>Supplier Code</label>
                  <input
                    type="text"
                    disabled
                    value={supplierCode}
                    className={`${inputClass} opacity-70 cursor-not-allowed`}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Supplier Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={supplierType}
                    onChange={(e) => setSupplierType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="A">Vendor Approved</option>
                    <option value="B">Vendor Non Approved</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Address 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={address1}
                    onChange={(e) => setAddress1(e.target.value)}
                    placeholder="Address line 1"
                  />
                </div>

                <div>
                  <label className={labelClass}>Address 2</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    placeholder="Address line 2"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={selectedCountry}
                    onChange={(e) => {
                      const cid = e.target.value;
                      setSelectedCountry(cid);
                      setSelectedState("");
                      setSelectedDistrict("");
                      setSelectedCity("");
                      setStates([]);
                      setDistricts([]);
                      setCities([]);
                      fetchStates(cid);
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.fld_countryid} value={c.fld_countryid}>
                        {c.fld_countryname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={selectedState}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedState(val);
                      setDivStateVisible(val === "other");
                      setSelectedDistrict("");
                      setSelectedCity("");
                      setDistricts([]);
                      setCities([]);
                      if (val !== "other" && val !== "") fetchDistricts(val);
                    }}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.fld_stateid} value={s.fld_stateid}>
                        {s.fld_statename}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {divStateVisible && (
                    <input
                      type="text"
                      className={`${inputClass} mt-2`}
                      placeholder="Enter new state"
                      value={stateText}
                      onChange={(e) => setStateText(e.target.value)}
                    />
                  )}
                </div>

                <div>
                  <label className={labelClass}>District</label>
                  <select
                    className={inputClass}
                    value={selectedDistrict}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedDistrict(val);
                      setDivDistrictVisible(val === "other");
                      setSelectedCity("");
                      setCities([]);
                      if (val !== "other" && val !== "") fetchCities(val);
                    }}
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.fld_districtid} value={d.fld_districtid}>
                        {d.fld_districtname}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {divDistrictVisible && (
                    <input
                      type="text"
                      className={`${inputClass} mt-2`}
                      placeholder="Enter new district"
                      value={districtText}
                      onChange={(e) => setDistrictText(e.target.value)}
                    />
                  )}
                </div>

                <div>
                  <label className={labelClass}>City</label>
                  <select
                    className={inputClass}
                    value={selectedCity}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedCity(val);
                      setDivCityVisible(val === "other");
                    }}
                  >
                    <option value="">Select City</option>
                    {cities.map((c) => (
                      <option key={c.fld_cityid} value={c.fld_cityid}>
                        {c.fld_cityname}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {divCityVisible && (
                    <input
                      type="text"
                      className={`${inputClass} mt-2`}
                      placeholder="Enter new city"
                      value={cityText}
                      onChange={(e) => setCityText(e.target.value)}
                    />
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    Zipcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={zipcode}
                    onChange={(e) => setZipcode(e.target.value)}
                    placeholder="Zip / Postal code"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    className={inputClass}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>

                <div>
                  <label className={labelClass}>Phone</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Phone number"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Mobile <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="Mobile number"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    TIN No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={tin}
                    onChange={(e) => setTIN(e.target.value)}
                    placeholder="TIN number"
                  />
                </div>

                <div>
                  <label className={labelClass}>GST No</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={gst}
                    onChange={(e) => setGST(e.target.value)}
                    placeholder="GST number"
                  />
                </div>

                <div>
                  <label className={labelClass}>PAN No</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={pan}
                    onChange={(e) => setPAN(e.target.value)}
                    placeholder="PAN number"
                  />
                </div>

                <div>
                  <label className={labelClass}>Opening Balance</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className={labelClass}>Ranking</label>
                  <select
                    className={inputClass}
                    value={ranking}
                    onChange={(e) => setRanking(e.target.value)}
                  >
                    <option value="">Select Ranking</option>
                    <option value="1">Bad</option>
                    <option value="2">Ok</option>
                    <option value="3">Fair</option>
                    <option value="4">Good</option>
                    <option value="5">Excellent</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    className={`${inputClass} resize-none`}
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Additional notes..."
                  />
                </div>

                <div>
                  <label className={labelClass}>Tally Supplier Header</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={tallyHeader}
                    onChange={(e) => setTallyHeader(e.target.value)}
                    placeholder="Tally header name"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-end gap-3 mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={resetForm}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <RotateCcw size={16} />
                Reset
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
              >
                <Save size={16} />
                {loading ? "Saving..." : "Update Supplier"}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {[
              { key: "statutory", label: "Statutory", icon: FileText },
              { key: "bank", label: "Bank", icon: CreditCard },
              { key: "contact", label: "Contact", icon: User },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = selectedTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setSelectedTab(tab.key)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold transition border-b-2 ${
                    isActive
                      ? `border-${baseColor}-600 text-${baseColor}-600 bg-${baseColor}-50/50 dark:bg-${baseColor}-900/20`
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>

                  {/* Tab Content */}
          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-lg font-bold capitalize">{selectedTab} Details</h3>
              </div>

              {/* ✅ FIX: Add button was missing entirely from the edit
                  page's tab section — there was no way to reach the
                  Statutory/Bank/Contact add screens from here, even
                  though those pages now require an existing supplier
                  and can only be reached via Edit. */}
              <button
                onClick={() => {
                  if (!supplierMainId) {
                    toast.error("Supplier ID missing — please reload this page.");
                    return;
                  }

                  if (selectedTab === "statutory") {
                    navigate("/inventory/Supplier-StatutoryAdd", {
                      state: { supplierid: supplierMainId },
                    });
                  } else if (selectedTab === "bank") {
                    navigate("/inventory/Supplier-BankAdd", {
                      state: { supplierid: supplierMainId },
                    });
                  } else if (selectedTab === "contact") {
                    navigate("/inventory/Supplier-ContactAdd", {
                      state: { supplierid: supplierMainId },
                    });
                  }
                }}
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
              >
                <Plus size={16} />
                Add
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>

                    {selectedTab === "statutory" && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase">Statutory Name</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase">Details</th>
                      </>
                    )}

                    {selectedTab === "bank" && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase">Bank Name</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase">Account No</th>
                      </>
                    )}

                    {selectedTab === "contact" && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase">Contact Person</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase">Designation</th>
                      </>
                    )}

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  </tr>
                </thead>

                <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                  {currentList.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-10 text-center text-gray-500"
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    currentList.map((item, index) => (
                      <tr
                        key={index}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium">{index + 1}</td>

                        {selectedTab === "statutory" && (
                          <>
                            <td className="px-4 py-3">{item.fld_statutoryhead}</td>
                            <td className="px-4 py-3">{item.fld_statutorydetail}</td>
                          </>
                        )}

                        {selectedTab === "bank" && (
                          <>
                            <td className="px-4 py-3">
                              {item.bank_name || item.fld_bankname}
                            </td>
                            <td className="px-4 py-3">
                              {item.account_no || item.fld_accountnumber}
                            </td>
                          </>
                        )}

                        {selectedTab === "contact" && (
                          <>
                            <td className="px-4 py-3">
                              {item.ContactName || item.fld_contactname}
                            </td>
                            <td className="px-4 py-3">
                              {item.Designation || item.fld_designation}
                            </td>
                          </>
                        )}

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded"
                            value={
                              selectedTab === "statutory"
                                ? item.fld_statutoryid
                                : selectedTab === "bank"
                                ? item.fld_bankid || item.fld_detailid
                                : item.fld_contactid
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default SupplierMainMasEdit;